import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import BookingLink from "./BookingLink";
import { BUSINESS, NAV } from "../lib/site";

function Logo() {
  return (
    <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center" aria-label={`${BUSINESS.name}. Home`}>
      <picture>
        <source type="image/webp" srcSet="/img/logo.jpg" />
        <img src="/img/logo.jpg" alt="Directive Optive Solutions, LLC logo" width="217" height="84" className="h-9 w-auto sm:h-11" />
      </picture>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "border-b border-line bg-white/90 backdrop-blur-md" : "bg-transparent"}`} data-testid="site-header">
        <div className="container-shell flex h-20 items-center justify-between gap-6">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `nav-link text-sm font-semibold text-ink transition-colors hover:text-pine ${isActive ? "text-pine" : ""}`}>
                {({ isActive }) => <span data-active={isActive}>{item.label}</span>}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <BookingLink page="header" className="hidden md:inline-flex items-center justify-center gap-2 rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-pine" data-testid="header-book-button">
              Book a Consultation <ArrowUpRight className="h-4 w-4" />
            </BookingLink>
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-expanded={open} aria-label={open ? "Close menu" : "Open menu"} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-olive lg:hidden">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="fixed inset-0 top-20 z-40 overflow-y-auto bg-sage lg:hidden" data-testid="mobile-menu">
            <div className="container-shell flex flex-col gap-1 py-10">
              {NAV.map((item, i) => (
                <motion.div key={item.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                  <NavLink to={item.to} data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className="block border-b border-line py-5 font-serif text-3xl text-olive">
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }} className="mt-8 flex flex-col gap-4">
                <BookingLink page="mobile-menu" className="btn-primary w-full" data-testid="mobile-book-button">Book a Consultation</BookingLink>
                <a href={BUSINESS.phoneHref} data-testid="mobile-phone-link" className="inline-flex items-center gap-2 text-sm font-semibold text-olive"><Phone className="h-4 w-4" /> {BUSINESS.phone}</a>
                <a href={BUSINESS.emailHref} data-testid="mobile-email-link" className="text-sm font-semibold text-olive">{BUSINESS.email}</a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
